/**
 * RSS Feed Selector Component
 * 
 * Allows users to select/deselect RSS feeds organized by category
 * Stores preferences in local storage
 */

import React, { useState, useEffect } from 'react';
import { Badge, Button } from '@tracekit/core';
import { CheckCircle, Circle, ChevronDown, ChevronRight, Search } from 'lucide-react';
import { RSS_FEED_REGISTRY, RSSFeedCategory, CATEGORY_NAMES, RSSFeedSource } from '../services/rssFeedRegistry';

interface RSSFeedSelectorProps {
  selectedFeedIds: string[];
  onSelectionChange: (feedIds: string[]) => void;
}

export function RSSFeedSelector({ selectedFeedIds, onSelectionChange }: RSSFeedSelectorProps) {
  const [expandedCategories, setExpandedCategories] = useState<Set<RSSFeedCategory>>(new Set(['government_contracts', 'procurement_usa']));
  const [searchTerm, setSearchTerm] = useState('');
  
  // Group feeds by category
  const feedsByCategory = RSS_FEED_REGISTRY.reduce((acc, feed) => {
    if (!acc[feed.category]) {
      acc[feed.category] = [];
    }
    acc[feed.category].push(feed);
    return acc;
  }, {} as Record<RSSFeedCategory, RSSFeedSource[]>);
  
  // Filter feeds by search term
  const filteredFeeds = searchTerm
    ? RSS_FEED_REGISTRY.filter(feed =>
        feed.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        feed.description.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : null;
  
  const toggleCategory = (category: RSSFeedCategory) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };
  
  const toggleFeed = (feedId: string) => {
    const newSelection = selectedFeedIds.includes(feedId)
      ? selectedFeedIds.filter(id => id !== feedId)
      : [...selectedFeedIds, feedId];
    onSelectionChange(newSelection);
  };
  
  const toggleCategory_AllFeeds = (category: RSSFeedCategory) => {
    const categoryFeeds = feedsByCategory[category].map(f => f.id);
    const allSelected = categoryFeeds.every(id => selectedFeedIds.includes(id));
    
    if (allSelected) {
      // Deselect all in category
      onSelectionChange(selectedFeedIds.filter(id => !categoryFeeds.includes(id)));
    } else {
      // Select all in category
      const newSelection = [...new Set([...selectedFeedIds, ...categoryFeeds])];
      onSelectionChange(newSelection);
    }
  };
  
  const selectAll = () => {
    onSelectionChange(RSS_FEED_REGISTRY.map(f => f.id));
  };
  
  const deselectAll = () => {
    onSelectionChange([]);
  };
  
  // If searching, show filtered results
  if (searchTerm && filteredFeeds) {
    return (
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search feeds..."
            className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-sm"
          />
        </div>
        
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {filteredFeeds.map(feed => (
            <FeedItem
              key={feed.id}
              feed={feed}
              selected={selectedFeedIds.includes(feed.id)}
              onToggle={() => toggleFeed(feed.id)}
            />
          ))}
        </div>
        
        <Button variant="primary" size="sm" onClick={() => setSearchTerm('')}>
          Clear Search
        </Button>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Search and Bulk Actions */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search feeds..."
            className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-sm"
          />
        </div>
        <Button variant="primary" size="sm" onClick={selectAll}>
          Select All
        </Button>
        <Button variant="primary" size="sm" onClick={deselectAll}>
          Deselect All
        </Button>
      </div>
      
      {/* Summary */}
      <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
        <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
          {selectedFeedIds.length} of {RSS_FEED_REGISTRY.length} feeds selected
        </span>
        <Badge variant="primary" className="text-xs">
          {Object.keys(feedsByCategory).length} categories
        </Badge>
      </div>
      
      {/* Category Groups */}
      <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
        {Object.entries(feedsByCategory).map(([category, feeds]) => {
          const isExpanded = expandedCategories.has(category as RSSFeedCategory);
          const selectedCount = feeds.filter(f => selectedFeedIds.includes(f.id)).length;
          const allSelected = selectedCount === feeds.length;
          
          return (
            <div key={category} className="border border-gray-200 dark:border-slate-700 rounded-lg overflow-hidden">
              {/* Category Header */}
              <div className="bg-gray-50 dark:bg-slate-800 p-3">
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => toggleCategory(category as RSSFeedCategory)}
                    className="flex items-center gap-2 flex-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                  >
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                    <span className="font-semibold text-sm">
                      {CATEGORY_NAMES[category as RSSFeedCategory]}
                    </span>
                    <Badge variant="primary" className="text-xs">
                      {selectedCount}/{feeds.length}
                    </Badge>
                  </button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleCategory_AllFeeds(category as RSSFeedCategory)}
                    className="text-xs"
                  >
                    {allSelected ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>
              </div>
              
              {/* Category Feeds */}
              {isExpanded && (
                <div className="p-2 space-y-1">
                  {feeds.map(feed => (
                    <FeedItem
                      key={feed.id}
                      feed={feed}
                      selected={selectedFeedIds.includes(feed.id)}
                      onToggle={() => toggleFeed(feed.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface FeedItemProps {
  feed: RSSFeedSource;
  selected: boolean;
  onToggle: () => void;
}

function FeedItem({ feed, selected, onToggle }: FeedItemProps) {
  const Icon = selected ? CheckCircle : Circle;
  
  // Consistent priority badge system
  const getPriorityBadge = (priority: number) => {
    if (priority === 1) {
      return { label: 'High Priority', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-red-300 dark:border-red-700' };
    } else if (priority === 2) {
      return { label: 'Medium Priority', className: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 border-yellow-300 dark:border-yellow-700' };
    } else if (priority === 3) {
      return { label: 'Standard', className: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 border-gray-300 dark:border-gray-600' };
    }
    return null;
  };
  
  const priorityBadge = getPriorityBadge(feed.priority);
  
  return (
    <button
      onClick={onToggle}
      className="w-full flex items-start gap-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors text-left"
    >
      <Icon className={`h-5 w-5 mt-0.5 shrink-0 ${selected ? 'text-green-600' : 'text-gray-400'}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
            {feed.name}
          </span>
          <div className="flex items-center gap-1 shrink-0">
            {priorityBadge && (
              <Badge variant="primary" className={`text-xs ${priorityBadge.className}`}>
                {priorityBadge.label}
              </Badge>
            )}
            <Badge variant="primary" className="text-xs">
              {feed.updateFrequency}
            </Badge>
          </div>
        </div>
        <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">
          {feed.description}
        </p>
      </div>
    </button>
  );
}

